'use client';

import React from 'react';
import {
  AuthorDocument,
  BlogDocument,
  SettingsDocument,
} from '../../../prismicio-types';
import { asText, RTHeading2Node } from '@prismicio/client';
import { Breadcrumb } from '../common/Breadcrumb';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { AuthorCard } from './AuthorCard';
import { ShareOnSocialMedia } from '../common/ShareOnSocialMedia';
import { PrismicNextImage } from '@prismicio/next';
import { BaseNewsletter } from '../blogHome/BaseNewsletter';
import { HorizontalVector } from '@/assets/logos/HorizontalVector';

interface Props {
  settings: SettingsDocument<string>;
  page: BlogDocument<string>;
  author: Partial<AuthorDocument>;
}

export const BlogPage = ({ settings, page, author }: Props) => {
  // State to track the currently active section in the table of contents
  const [activeSection, setActiveSection] = React.useState('introduction');

  const blogSections = page.data.content
    .filter((block) => block.type === 'heading2')
    .map((block) => asText([block as RTHeading2Node]));

  const slugify = (text: string) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleScrollTo = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    } else {
      console.error(`[BlogPage] FAILED to find element with ID: "${id}"`);
    }
  };

  /**
   * Handles scrolling to the top of the main blog content area.
   */
  const handleScrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById('blog-content-container');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Set 'introduction' as the active section
    setActiveSection('introduction');
  };

  // Explicitly define the serializer map
  const components: JSXMapSerializer = {
    heading2: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h2 id={id} className="scroll-mt-24">
          {children}
        </h2>
      );
    },
  };

  const renderBlogHeader = () => {
    const headerStyle = page.data.image.url
      ? {
          backgroundImage: `linear-gradient(to bottom, rgba(14, 17, 16, 0.9), rgba(15, 57, 50, 0.7)), url(${page.data.image.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {};

    return (
      <div
        id="children-container"
        className="flex flex-col w-full items-center"
        style={headerStyle}
      >
        <Breadcrumb
          page={page}
          showHome={true}
          className="pt-24 md:pt-32 text-[#05F273]"
          classNameLink="ml-4 font-light text-[#05F273]"
          colorIcon="#05F273"
        />

        <div className="w-full flex flex-col items-center py-12 md:py-20">
          <div className="w-full text-white px-6 sm:px-10 md:px-20 lg:px-32 text-center pb-16 md:pb-24">
            <PrismicRichText field={page.data.card_title} />
          </div>
        </div>
      </div>
    );
  };

  const renderAuthorCard = () => {
    return (
      <div className="flex flex-col items-center w-full">
        <AuthorCard
          variation="blog-post"
          date={page.last_publication_date}
          avatar={author.data?.avatar}
          duration={page.data.read_duration}
          authorUid={author.uid}
        />
      </div>
    );
  };

  const renderContentInfo = () => {
    return (
      <div className="hidden md:flex justify-start items-start w-1/3 sticky top-[6rem]">
        <div
          className="flex flex-col items-start gap-4 w-4/5 md:w-3/4 lg:w-3/4 rounded-xl pt-8 px-6"
          style={{
            background:
              'linear-gradient(to right, rgba(255,255,255,0.15), rgba(3,191,158,0.15), rgba(255,255,255,0.15))',
          }}
        >
          <div>
            <span className="font-bold">Content</span>
          </div>
          <div className="pl-[1rem]">
            <a
              href="#"
              onClick={handleScrollToTop}
              className="text-left cursor-pointer flex flex-row items-center gap-2"
            >
              {activeSection === 'introduction' && (
                <HorizontalVector className="flex-shrink-0" />
              )}
              <span
                className={
                  activeSection === 'introduction'
                    ? 'font-bold text-[#05F273]'
                    : 'font-light'
                }
              >
                Introduction
              </span>
            </a>

            {blogSections && blogSections.length > 0 && (
              <div className="flex flex-col items-start">
                {blogSections.map((sectionName, index) => {
                  const slug = slugify(sectionName);
                  return (
                    <a
                      key={index}
                      href={`#${slug}`}
                      onClick={(e) => handleScrollTo(e, slug)}
                      className="text-left cursor-pointer flex flex-row items-start justify-start gap-2"
                    >
                      {activeSection === slug && (
                        <HorizontalVector className="flex-shrink-0 mt-[0.5rem]" />
                      )}
                      <span
                        className={
                          activeSection === slug
                            ? 'font-bold text-[#05F273]'
                            : ''
                        }
                      >
                        {sectionName}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-col items-center gap-4 w-full">
            <div className="border-b-2 border-[#002726] w-full" />
            <div className="">
              <span className="font-bold text-center">Authors</span>
            </div>
            {renderAuthorCard()}
            <div className="border-b-2 border-[#002726] w-full" />
          </div>
          <div className="flex flex-col w-full items-center gap-4 pb-8">
            <span className="font-semibold">Share this article</span>
            <ShareOnSocialMedia
              className="flex gap-2 text-white"
              bgColor="bg-[#002726]"
              fill="white"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderBlogPost = () => {
    return (
      <div className="flex flex-col gap-8 w-full md:w-2/3 px-4 md:pl-8">
        <div className="flex justify-center">
          <PrismicNextImage
            field={page.data.image}
            className="rounded-[20px] w-full"
          />
        </div>
        <PrismicRichText field={page.data.content} components={components} />
        {renderBlogFooter()}
      </div>
    );
  };

  const renderBlogContent = () => {
    return (
      <div
        id="blog-content-container"
        className="flex flex-row items-start justify-center pt-12 gap-[4rem] scroll-mt-[6rem]"
      >
        {renderBlogPost()}
        {renderContentInfo()}
      </div>
    );
  };

  const renderBlogFooter = () => {
    return (
      <div className="flex flex-col w-full gap-4 pb-16 pt-8 items-center">
        <div className="flex flex-row justify-center border-b-2 border-t-2 border-[#002726] w-full py-[1rem]">
          <ShareOnSocialMedia
            className="flex gap-2"
            bgColor="bg-[#002726]"
            fill="white"
          />
        </div>
        <BaseNewsletter
          button_label="Subscribe"
          disclaimer="By subscribing you agree to with our Privacy Policy and provide consent to receive updates from our company."
          email_placeholder="Enter your email"
          formId={`${settings.data.default_newsletter_form_id}`}
          hubspotPortalIdForm={`${settings.data.hubspotPortalIdForm}`}
          invite_text="Stay informed with the latest insights and trends in the software development industry."
          title="Subscribe to our newsletter"
          variation="forBlogEntry"
        />
      </div>
    );
  };

  return (
    <div className="w-full mx-auto">
      {renderBlogHeader()}
      <div className="bg-[#0E1110] text-white w-full flex flex-col items-center">
        <div className=" max-w-screen-xl">{renderBlogContent()}</div>
      </div>
    </div>
  );
};
